<template>
  <div
    class="border-static-default-mid rounded-sm flex w-full flex-col justify-between gap-12 border p-24"
  >
    <!-- Title -->
    <p class="body-1 flex min-h-52 items-start gap-8">
      <slot name="headingSection"></slot>
    </p>

    <ul v-if="nonCurrencyData" class="mb-auto">
      <li>
        <div class="flex items-center justify-between gap-8">
          <div class="flex items-baseline gap-4">
            <span class="heading-2">
              {{ nonCurrencyData.currentPeriod.count }}
            </span>
            <span class="text-caption"
              ><KpiIndicator :kpi="nonCurrencyData.currentPeriod.kpi" size="16"
            /></span>
          </div>
          <span class="text-static-default-low text-caption">
            {{
              formatDateRangeForInsightBlock(
                timePeriod.currentPeriod.startDate,
                timePeriod.currentPeriod.endDate,
                i18n,
              )
            }}</span
          >
        </div>
        <div class="-mt-6 flex justify-between gap-8">
          <span class="text-static-default-low text-caption">
            {{ nonCurrencyData.previousPeriod.count }}</span
          >
          <span class="text-static-default-low text-caption">
            {{
              formatDateRangeForInsightBlock(
                timePeriod.previousPeriod.startDate,
                timePeriod.previousPeriod.endDate,
                i18n,
              )
            }}</span
          >
        </div>
        <RevDivider class="my-8" />
        <div class="flex items-center justify-between gap-8">
          <div class="flex items-center gap-4">
            <span class="heading-2">
              {{ nonCurrencyData.yearOverYear.count }}
            </span>
            <span class="text-caption">
              <KpiIndicator :kpi="nonCurrencyData.yearOverYear.kpi" size="12" />
            </span>
          </div>
          <span class="text-static-default-low text-caption">
            {{
              formatDateRangeForInsightBlock(
                timePeriod.yearOverYear.startDate,
                timePeriod.yearOverYear.endDate,
                i18n,
              )
            }}</span
          >
        </div>
      </li>
    </ul>

    <ul v-if="singleCurrencyData" class="mb-auto">
      <li
        v-for="(item, index) in singleCurrencyData"
        :key="`${item.currentPeriod.currency}-${index}`"
      >
        <div class="flex items-center justify-between gap-8">
          <div class="flex items-baseline gap-4">
            <span class="heading-2">
              {{
                i18n.price(item.currentPeriod.amount, {
                  currency: item.currentPeriod.currency,
                })
              }}</span
            >
            <span class="text-caption"
              ><KpiIndicator :kpi="item.currentPeriod.kpi" size="16"
            /></span>
          </div>
          <span class="text-static-default-low text-caption">
            {{
              formatDateRangeForInsightBlock(
                timePeriod.currentPeriod.startDate,
                timePeriod.currentPeriod.endDate,
                i18n,
              )
            }}</span
          >
        </div>
        <div class="-mt-6 flex items-center justify-between gap-8">
          <span class="text-static-default-low text-caption">
            {{
              i18n.price(item.previousPeriod.amount, {
                currency: item.previousPeriod.currency,
              })
            }}</span
          >
          <span class="text-static-default-low text-caption">
            {{
              formatDateRangeForInsightBlock(
                timePeriod.previousPeriod.startDate,
                timePeriod.previousPeriod.endDate,
                i18n,
              )
            }}</span
          >
        </div>
        <RevDivider class="my-8" />
        <div class="flex items-center justify-between gap-8">
          <div class="flex items-center gap-4">
            <span class="heading-2">
              {{
                i18n.price(item.yearOverYear.amount, {
                  currency: item.yearOverYear.currency,
                })
              }}</span
            >
            <span class="caption-bold">
              <KpiIndicator :kpi="item.yearOverYear.kpi" size="12" />
            </span>
          </div>
          <span class="text-static-default-low text-caption">
            {{
              formatDateRangeForInsightBlock(
                timePeriod.yearOverYear.startDate,
                timePeriod.yearOverYear.endDate,
                i18n,
              )
            }}</span
          >
        </div>
      </li>
    </ul>

    <!-- If there are multiple currencies-->
    <ul v-if="multipleCurrenciesData">
      <li
        v-for="(item, index) in multipleCurrenciesData"
        :key="`${item.currentPeriod.currency}-${index}`"
      >
        <div class="flex items-center justify-between gap-8">
          <div class="flex items-baseline gap-4">
            <span class="heading-2">
              {{
                i18n.price(item.currentPeriod.amount, {
                  currency: item.currentPeriod.currency,
                })
              }}</span
            >
            <span class="text-caption"
              ><KpiIndicator :kpi="item.currentPeriod.kpi" size="16"
            /></span>
          </div>
          <span class="text-static-default-low text-caption">
            {{
              formatDateRangeForInsightBlock(
                timePeriod.currentPeriod.startDate,
                timePeriod.currentPeriod.endDate,
                i18n,
              )
            }}</span
          >
        </div>
        <div class="-mt-6 mb-4 flex items-center justify-between gap-8">
          <span class="text-static-default-low text-caption">
            {{
              i18n.price(item.previousPeriod.amount, {
                currency: item.previousPeriod.currency,
              })
            }}</span
          >
          <span class="text-static-default-low text-caption">
            {{
              formatDateRangeForInsightBlock(
                timePeriod.previousPeriod.startDate,
                timePeriod.previousPeriod.endDate,
                i18n,
              )
            }}</span
          >
        </div>
        <div class="flex items-center justify-between gap-8">
          <div class="flex items-center gap-4">
            <span class="caption-bold">
              {{
                i18n.price(item.yearOverYear.amount, {
                  currency: item.yearOverYear.currency,
                })
              }}</span
            >
            <span class="text-caption">
              <KpiIndicator :kpi="item.yearOverYear.kpi" size="12" />
            </span>
          </div>
          <span class="text-static-default-low text-caption">
            {{
              formatDateRangeForInsightBlock(
                timePeriod.yearOverYear.startDate,
                timePeriod.yearOverYear.endDate,
                i18n,
              )
            }}</span
          >
        </div>
        <RevDivider
          v-if="index !== multipleCurrenciesData.length - 1"
          class="my-8"
        />
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevDivider } from '@ds/components/Divider'

import type { GetSalesMetricsResponse } from '~/api/sales-metrics/types'

import { formatDateRangeForInsightBlock } from '../utils/SalesInsightUtils'

import KpiIndicator from './KpiIndicator.vue'

const i18n = useI18n()

const props = defineProps<{
  timePeriod: GetSalesMetricsResponse['timePeriods']
  metricData:
    | GetSalesMetricsResponse['salesRevenue']
    | GetSalesMetricsResponse['refundedAmount']
    | GetSalesMetricsResponse['receivedOrders']
}>()

// Metrics data for a single currency
const singleCurrencyData = computed(() => {
  if (Array.isArray(props.metricData) && props.metricData.length === 1) {
    return props.metricData
  }

  return null
})

// Metrics data for multiple currencies
const multipleCurrenciesData = computed(() => {
  if (Array.isArray(props.metricData) && props.metricData.length > 1) {
    return props.metricData
  }

  return null
})

// Metrics data for non-currency data, such as the number of orders shipped
const nonCurrencyData = computed(() => {
  if (
    !Array.isArray(props.metricData) &&
    Object.keys(props.metricData).length > 0
  ) {
    return props.metricData
  }

  return null
})
</script>
